import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  PushPin as PushPinIcon,
  Campaign as CampaignIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface Reply {
  id: string;
  type: 'reply';
  parent_id: string;
  author_id: string;
  author_name: string;
  content: string;
  is_deleted: boolean;
  edited_at: string | null;
  edited_by: 'author' | 'admin' | null;
  created_at: string;
  updated_at: string;
}

interface Post {
  id: string;
  type: 'post';
  author_id: string;
  author_name: string;
  title: string;
  content: string;
  category: 'general' | 'transportation' | 'accommodations' | 'other';
  is_pinned: boolean;
  is_announcement: boolean;
  is_deleted: boolean;
  edited_at: string | null;
  edited_by: 'author' | 'admin' | null;
  created_at: string;
  updated_at: string;
}

interface PostDetailProps {
  postId: string;
  currentUserId?: string;
  isAdmin?: boolean;
  onBack?: () => void;
  onEdit?: (postId: string) => void;
  onDelete?: (postId: string) => void;
  onPin?: (postId: string, isPinned: boolean) => void;
}

export const PostDetail: React.FC<PostDetailProps> = ({
  postId,
  currentUserId,
  isAdmin = false,
  onBack,
  onEdit,
  onDelete,
  onPin
}) => {
  const [post, setPost] = useState<Post | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState<string | null>(null);

  const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod'
    : 'http://localhost:3001';

  useEffect(() => {
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/api/message-board/posts/${postId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Post not found');
        }
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error('Failed to load post');
      }

      const result = await response.json();
      if (result.success) {
        setPost(result.data.post);
        setReplies(result.data.replies || []);
      } else {
        throw new Error(result.error || 'Failed to load post');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) {
      setError('Reply content cannot be empty');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/posts/${postId}/replies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit reply');
      }

      setReplyContent('');
      setShowReplyForm(false);
      await loadPost();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    try {
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      if (onDelete) {
        onDelete(postId);
      } else if (onBack) {
        onBack();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/posts/${replyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete reply');
      }

      setReplyToDelete(null);
      await loadPost();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleTogglePin = async () => {
    if (!post) return;

    try {
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/posts/${postId}/pin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_pinned: !post.is_pinned })
      });

      if (!response.ok) {
        throw new Error('Failed to update pin status');
      }

      if (onPin) {
        onPin(postId, !post.is_pinned);
      }
      await loadPost();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'general': return 'default';
      case 'transportation': return 'primary';
      case 'accommodations': return 'secondary';
      case 'other': return 'info';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const canEditPost = () => {
    return post && (post.author_id === currentUserId || isAdmin);
  };

  const canDeletePost = () => {
    return post && (post.author_id === currentUserId || isAdmin);
  };

  const canEditReply = (reply: Reply) => {
    return reply.author_id === currentUserId || isAdmin;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !post) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Posts
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Posts
        </Button>
        <Alert severity="warning">Post not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Back Button */}
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Posts
      </Button>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Post Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Post Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {post.is_pinned && (
                  <Chip
                    icon={<PushPinIcon />}
                    label="Pinned"
                    size="small"
                    color="primary"
                  />
                )}
                {post.is_announcement && (
                  <Chip
                    icon={<CampaignIcon />}
                    label="Announcement"
                    size="small"
                    color="warning"
                  />
                )}
                <Chip
                  label={getCategoryLabel(post.category)}
                  size="small"
                  color={getCategoryColor(post.category) as any}
                />
              </Box>
              <Typography variant="h5" component="h1" gutterBottom>
                {post.title}
              </Typography>
            </Box>
          </Box>

          {/* Post Content */}
          <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
            {post.content}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Post Meta */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {post.author_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {formatDate(post.created_at)}
              </Typography>
              {post.edited_at && (
                <Chip
                  label={`Edited by ${post.edited_by}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<ReplyIcon />}
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              Reply
            </Button>
            {canEditPost() && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit && onEdit(postId)}
              >
                Edit
              </Button>
            )}
            {canDeletePost() && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<PushPinIcon />}
                onClick={handleTogglePin}
              >
                {post.is_pinned ? 'Unpin' : 'Pin'}
              </Button>
            )}
          </Box>

          {/* Reply Form */}
          {showReplyForm && (
            <Box mt={3} p={2} sx={{ backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Write a reply
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Type your reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                sx={{ mb: 2, backgroundColor: 'background.paper' }}
              />
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleSubmitReply}
                  disabled={submitting || !replyContent.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit Reply'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowReplyForm(false);
                    setReplyContent('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Replies Section */}
      <Typography variant="h6" gutterBottom>
        Replies ({replies.length})
      </Typography>

      {replies.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No replies yet. Be the first to reply!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {replies.map((reply) => (
            <Card key={reply.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="bold">
                      {reply.author_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {formatDate(reply.created_at)}
                    </Typography>
                    {reply.edited_at && (
                      <Chip
                        label={`Edited by ${reply.edited_by}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {canEditReply(reply) && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setReplyToDelete(reply.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {reply.content}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Delete Post Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this post? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePost} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Reply Confirmation Dialog */}
      <Dialog open={!!replyToDelete} onClose={() => setReplyToDelete(null)}>
        <DialogTitle>Delete Reply</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this reply? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyToDelete(null)}>Cancel</Button>
          <Button
            onClick={() => replyToDelete && handleDeleteReply(replyToDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
